import { useCallback, useEffect, useRef, useState } from 'react';
import Form, {
    GroupItem,
    SimpleItem,
    RequiredRule,
    AsyncRule,
    Label,
} from 'devextreme-react/form';
import 'devextreme-react/autocomplete';
import 'devextreme-react/date-range-box';
import { Col, Row } from 'react-bootstrap';
import { roleService } from '@/core/services/role-service';
import { applicationService } from '@/core/services/application-service';
import { accountService } from '@/core/services/account-service';
import { menuservice } from '@/core/services/menu-service';
import { permissionService } from '@/core/services/permission-service';
import { useNavigate, useParams } from 'react-router-dom';

import { IRole } from '@/core/interfaces/usestate-interfaces';
import { t } from 'i18next';
import DataGrid, { Column, Paging, Editing, Grouping } from 'devextreme-react/data-grid';
import {
    TreeList, Editing as TlEditing, Column as TlColumn, Button, TreeListTypes,
} from 'devextreme-react/tree-list';
import { List, Button as DXButton, Popup, TextBox } from 'devextreme-react';
import { ELEMENT_ID } from '@/core/constants/common/element-constants';
import clsx from 'clsx';
import { TextBoxTypes } from 'devextreme-react/cjs/text-box';
import { Required } from '@/core/helper/validation-rule-helper';
import FormWrapper from '@/core/wrappers/form-wrapper';
import CustomStore from 'devextreme/data/custom_store';

const colCountByScreen = {
    xs: 1,
    sm: 1,
    md: 2,
    lg: 2,
};

function sendCheckRoleRequest(email: string) {
    return new Promise(async (resolve) => {
        resolve(true);
    });
}

const asyncRoleValidation = (params: { value: any; }) => sendCheckRoleRequest(params.value);

function RoleDefinition() {
    const formRef: any = useRef<Form>();
    const submitFormRef: any = useRef<HTMLFormElement>();
    const [role, setRole] = useState<IRole>({ id: '', name: '', description: '', applicationId: '', accountId: '', permissions: [], menus: [] });
    const [rolePermissions, setRolePermissions] = useState<any[]>([]);
    const [applications, setApplications] = useState<CustomStore | null>(null);
    const [accounts, setAccounts] = useState([]);
    const [allPermissions, setAllPermissions] = useState([]);
    const [permissions, setPermissions] = useState<any[]>([]);
    const [permissionSubjects, setPermissionSubjects] = useState<object[]>([]);
    const [filteredPermissionSubjects, setFilteredPermissionSubjects] = useState<object[]>([]);
    const [selectedMenus, setSelectedMenus] = useState<any[]>([]);
    const [selectedPermissionValue, setSelectedPermissionValue] = useState<any>(null);
    const [menus, setMenus] = useState<any[]>([]);
    const [popupVisible, setPopupVisible] = useState(false);
    const [selectedPermissionName, setSelectedPermissionName] = useState<string>();
    const [selectedPermission, setSelectedPermission] = useState<any>(null);
    const [searchTextBox, setSearchTextBox] = useState<any>("");
    const [saveButtonEnabled, setSaveButtonEnabled] = useState<boolean>(false);
    const [tab, setTab] = useState('Main')
    const [selectedApp, setSelectedApp] = useState<any>(null);
    const params = useParams();
    const roleId = params.id;
    const navigate = useNavigate();
    useEffect(() => {
        if (roleId && role.id == '') {
            (async function () {
                const getRole = await roleService.getRole(roleId)
                const data = getRole.data;
                setRole({ id: data.id, name: data.name, description: data.description, applicationId: data.applicationId, accountId: data.accountId, permissions: data.permissions, menus: data.menus });
                const permInRole = data.permissions.filter((item: any) => item.valueType !== 'Menu').sort((a: any, b: any) => a.valueType.localeCompare(b.valueType))

                setRolePermissions(permInRole)

            })();
        }
    }, []);


    useEffect(() => {
        setApplications(new CustomStore({
            key: 'id',
            cacheRawData: true,
            load: applicationService.lookupApplication,
            useDefaultSearch: true,
            byKey: async (key, loadOptions: any) => {
                var returnObject = null;
                if (key) {
                    loadOptions.filter = ["id", "=", key];
                    const objectsByID = (await applicationService.lookupApplication(loadOptions))
                    returnObject = objectsByID.data.length > 0 ? objectsByID.data[0] : null;
                }
                return returnObject
            },
        }));
    }, []);

    useEffect(() => {
        const fetchAccount = async () => {
            const account = await accountService.lookupAccounts({});
            setAccounts(account.data)
        };
        fetchAccount();
    }, []);



    useEffect(() => {
        const fetchPermission = async () => {
            const permissions = await permissionService.lookupPermission({ filter: ["ApplicationId", "=", selectedApp.id] });

            const distinctSubjects = permissions.data.filter((item: any) => item.valueType !== 'Menu').reduce((acc: any, item: any) => {
                const { subject, ...rest } = item;
                const existingGroup = acc.find((group: any) => group.subject === subject);

                if (existingGroup) {
                    existingGroup.items.push(rest);
                } else {
                    acc.push({ subject, items: [rest] });
                }

                return acc;
            }, [])

            setPermissionSubjects(distinctSubjects.sort((a: any, b: any) => a.subject.localeCompare(b.subject)));
            setFilteredPermissionSubjects(distinctSubjects.sort((a: any, b: any) => a.subject.localeCompare(b.subject)));


            const allPermissionList = permissions.data.filter((item: any) => item.valueType !== 'Menu').map((item: any) => ({
                id: item.id,
                name: item.name,
                subject: item.subject,
                valueType: item.valueType,
                actions: JSON.parse(item.valueConfig).options
            }));

            const permissionWithConfig = allPermissionList.map((item: any) => {
                const newItem = { ...item };
                if (item.valueType === "Actions") {
                    item.actions.forEach((action: any) => {
                        newItem[action.toLowerCase()] = false;
                    });

                }
                else {
                    newItem.value = "";
                }
                delete newItem.actions;
                return newItem;
            });
            setAllPermissions(permissionWithConfig);
        };
        if (selectedApp && selectedApp.id) {
            fetchPermission();
        }
    }, [selectedApp]);

    useEffect(() => {
        var menuIds = getMenuIds(selectedMenus)
        formRef.current.props.formData.menus = menuIds
    }, [selectedMenus]);



    useEffect(() => {
        const fetchMenu = async () => {
            const menuResult = await menuservice.lookupMenu({ applicationId: selectedApp.id });
            var menuSelected = []
            const roleMenuPermission = role.permissions.find((item: any) => item.valueType === 'Menu')

            if (roleMenuPermission) {
                const roleMenus = JSON.parse(roleMenuPermission.permissionValue).value
                for (let menuId of roleMenus) {

                    const foundHierarchy = findItemById(menuId, menuResult.data);
                    if (foundHierarchy) {
                        const menu = buildHierarchy(foundHierarchy);
                        menuResult.data = removeItemById(menuId, menuResult.data);
                        setMenus(menuResult.data);
                        const newMenus = [...menuSelected]
                        menuSelected = deepMergeObjectWithArray(menu, newMenus);
                    }

                }
            }
            setSelectedMenus(menuSelected.sort((a: any, b: any) => a.order - b.order));
            setMenus(menuResult.data.sort((a: any, b: any) => a.order - b.order));


        };
        if (selectedApp && selectedApp.id) {
            fetchMenu();
        }
    }, [selectedApp]);

    const handleSubmit = useCallback(async (): Promise<void> => {
        return new Promise(async (resolve, reject) => {
            try {
                setTab('Main')
                const roleForm = { ...formRef.current.props.formData };

                const permissionWithValue = rolePermissions.map((item: any) => {
                    const newItem = { ...item };
                    if (item.valueType === "Actions") {
                        JSON.parse(item.permissionValue).value.forEach((action: any) => {
                            newItem[action.toLowerCase()] = true;
                        });

                    }
                    delete newItem.actions;
                    return newItem;
                });

                roleForm.permissions = permissionWithValue;

                if (roleId) {
                    await roleService.updateRole(null, roleForm);
                } else {
                    await roleService.createRole(roleForm);
                    navigate(`/role-list/`, {})
                }
                resolve();
            } catch (error) {
                reject(error)
            }

        })
    }, [rolePermissions, selectedMenus]);


    const nameEditorOptions = {
        inputAttr: { [ELEMENT_ID]: 'name' },
        disabled: roleId ? true : false
    };

    const descriptionEditorOptions = {
        inputAttr: { [ELEMENT_ID]: 'description' },
    };

    const applicationEditorOptions = {
        inputAttr: { [ELEMENT_ID]: 'application' },
        dataSource: applications,
        displayExpr: 'name',
        valueExpr: 'id',
        placeholder: undefined,
        onSelectionChanged: (e: any) => {
            if (!selectedApp || selectedApp.id !== e.selectedItem.id) {
                setSelectedApp(e.selectedItem);
            }
        }
    };

    const accountEditorOptions = {
        inputAttr: { [ELEMENT_ID]: 'account' },
        dataSource: accounts,
        displayExpr: 'name',
        valueExpr: 'id',
        placeholder: undefined,
    };



    function ItemTemplate(data: any) {
        return <div>{data.name}</div>;
    }

    function onSelectedItemsChange(data: any) {
        if (data.length > 0) {
            setSelectedPermissionValue(data[0].items[0].valueType)
            const allPerms = [...allPermissions]
            const selected: any = allPerms.find((item: any) => item.id === data[0].items[0].id)


            if (selected) {
                if (data[0].items[0].valueType === "Actions") {
                    selected["create"] = selected["create"] ? false : selected["create"]
                    selected["update"] = selected["update"] ? false : selected["update"]
                    selected["delete"] = selected["delete"] ? false : selected["delete"]
                    selected["list"] = selected["list"] ? false : selected["list"]
                    selected["read"] = selected["read"] ? false : selected["read"]
                }
                else {
                    selected["value"] = "";
                }
                setPermissions([selected]);
                setSelectedPermissionName(selected["name"])
            }
        }
    }

    function onEditorPreparing(e: any) {
        if (e.row.data[e.dataField] == undefined) {
            e.editorOptions.disabled = true;
        }

    }

    function onRowUpdating(e: any) {
        var data = { ...e.data };
        setSelectedPermission(data)
        setSaveButtonEnabled(true);

    }


    function findItemById(id: any, items: any, parentHierarchy: any[] = []): any {
        for (let item of items) {
            let anyItem: any = { ...item };
            if (item.id === id) {
                return [...parentHierarchy, item];
            }
            if (item.items && item.items.length > 0) {
                anyItem.items = [];
                const found = findItemById(id, item.items, [...parentHierarchy, anyItem]);
                if (found) {
                    return found;
                }
            }
        }
        return null;
    }

    function buildHierarchy(items: any): any {

        if (items.length === 0) return null;

        const [current, ...rest] = items;
        const newItem = { ...current, items: [] };

        if (rest.length > 0) {
            newItem.items.push(buildHierarchy(rest));
        }

        if (current.items.length > 0) {
            newItem.items = newItem.items.concat(current.items);
        }

        return newItem;
    }

    function removeItemById(id: any, items: any[]): any[] {
        return items.reduce((acc: any[], item: any) => {
            if (item.id === id) {
                return acc;
            }
            if (item.items && item.items.length > 0) {
                item.items = removeItemById(id, item.items);
                if (item.items.length > 0) {
                    acc.push(item);
                }
            } else {
                acc.push(item);
            }
            return acc;
        }, []);
    }





    function mergeObjectWithArray(obj: any, arr: any): boolean {
        var result = false
        arr.forEach((itemB: any) => {
            if (obj.id === itemB.id) {
                result = true;
                mergeItems(obj.items, itemB.items);
            }
        });
        return result;
    }

    function getMenuIds(menu: any) {
        const ids: any = [];

        function traverse(node: any) {
            if (!!node.path) {
                ids.push(node.id);
            }
            if (node.items) {
                for (const item of node.items) {
                    traverse(item);
                }
            }
        }

        for (const rootNode of menu) {
            traverse(rootNode);
        }

        return ids;
    }

    function mergeItems(itemsA: any, itemsB: any) {
        itemsB.forEach((itemB: any) => {
            const matchingItemA = itemsA.find((itemA: any) => itemA.id === itemB.id);
            if (matchingItemA) {
                mergeItems(matchingItemA.items, itemB.items);
            } else {
                itemsA.push(itemB);
            }
        });
    }


    function deepMergeObjectWithArray(obj: any, arr: any) {
        const merged = JSON.parse(JSON.stringify(obj));
        const result = mergeObjectWithArray(merged, arr);
        if (result) {

            const index = arr.findIndex((item: any) => item.id === merged.id);
            arr.splice(index, 1, merged);
        }
        else {
            arr.push(merged);
        }
        return arr;
    }

    const selectMenu = (e: any) => {
        const foundHierarchy = findItemById(e.row?.data.id, menus);
        const menu = buildHierarchy(foundHierarchy);
        setMenus(removeItemById(e.row?.data.id, menus));
        const newMenus = [...selectedMenus]
        var menuSelected = deepMergeObjectWithArray(menu, newMenus);
        setSelectedMenus(menuSelected);
    }

    const deSelectMenu = (e: any) => {
        const foundHierarchy = findItemById(e.row?.data.id, selectedMenus);
        const menu = buildHierarchy(foundHierarchy);
        setSelectedMenus(removeItemById(e.row?.data.id, selectedMenus));
        const newMenus = [...menus]
        var menuDeselected = deepMergeObjectWithArray(menu, newMenus);
        setMenus(menuDeselected);
    }

    const hideInfo = async () => {
        setPopupVisible(false);
    };

    const showAddPermission = async () => {
        setFilteredPermissionSubjects(permissionSubjects);
        setPopupVisible(true)
    }

    const showEditPermission = async () => {
        setPopupVisible(true)
    }

    const closeAddPermission = async () => {
        setSelectedPermission(null);
        setSearchTextBox("");
        setPermissions([])
        setSelectedPermissionName("")
        setFilteredPermissionSubjects([]);
        setSelectedPermissionValue("");
        setPopupVisible(false);
    }

    const addPermission = async () => {

        const rolePerms = [...rolePermissions]
        if (selectedPermission.valueType === 'Actions') {
            const value = []
            selectedPermission["create"] && value.push("Create")
            selectedPermission["update"] && value.push("Update")
            selectedPermission["delete"] && value.push("Delete")
            selectedPermission["read"] && value.push("Read")
            selectedPermission["list"] && value.push("List")
            selectedPermission.permissionValue = JSON.stringify({ value });
        }
        else {
            selectedPermission.permissionValue = JSON.stringify({ value: selectedPermission.value });
        }


        const index = rolePerms.findIndex((item: any) => item.id === selectedPermission.id);
        if (index !== -1) {
            rolePerms.splice(index, 1, selectedPermission);

        }
        else {
            rolePerms.push(selectedPermission);
        }
        setRolePermissions(rolePerms)
        closeAddPermission();
        setSaveButtonEnabled(false);
        //formRef.current.props.formData.permissions = selectedPermissions;
    }

    const GroupTemplate = (item: any) => <div> {item.subject}</div>;

    function searchItemsByName(data: any, searchString: any) {
        return data.reduce((acc: any, curr: any) => {
            const matchingItems = curr.items.filter((item: any) => item.name.toLowerCase().includes(searchString.toLowerCase()));
            if (matchingItems.length > 0) {
                acc.push({
                    subject: curr.subject,
                    items: matchingItems
                });
            }
            return acc;
        }, []);
    }

    function searchItemsById(data: any, searchString: any) {
        return data.reduce((acc: any, curr: any) => {
            const matchingItems = curr.items.filter((item: any) => item.id.includes(searchString));
            if (matchingItems.length > 0) {
                acc.push({
                    subject: curr.subject,
                    items: matchingItems
                });
            }
            return acc;
        }, []);
    }

    const searchPermission = async (data: TextBoxTypes.ValueChangedEvent) => {
        setSearchTextBox(data.value)
        var filtered = []
        if (!!data.value)
            filtered = searchItemsByName(permissionSubjects, data.value)
        setFilteredPermissionSubjects(filtered.sort((a: any, b: any) => a.subject.localeCompare(b.subject)));
    }


    const editRolePermission = (e: any) => {
        showEditPermission();
        var filtered = searchItemsById(permissionSubjects, e.row.data.id)
        setSelectedPermission(e.row.data)
        const selectedPerm = { ...e.row.data }

        const perm = allPermissions.find((x: any) => x.id === e.row.data.id)
        if (perm) {

            if (e.row.data.valueType === 'Actions') {
                selectedPerm["create"] = perm["create"] === false ? e.row.data.permissionValue.includes("Create") : undefined;
                selectedPerm["update"] = perm["update"] === false ? e.row.data.permissionValue.includes("Update") : undefined;
                selectedPerm["delete"] = perm["delete"] === false ? e.row.data.permissionValue.includes("Delete") : undefined;
                selectedPerm["read"] = perm["read"] === false ? e.row.data.permissionValue.includes("Read") : undefined;
                selectedPerm["list"] = perm["list"] === false ? e.row.data.permissionValue.includes("List") : undefined;
            } else {
                selectedPerm.value = JSON.parse(e.row.data.permissionValue).value
            }

            setPermissions([selectedPerm])
            setSelectedPermissionName(e.row.data.name)
            setFilteredPermissionSubjects(filtered.sort((a: any, b: any) => a.subject.localeCompare(b.subject)));
            setSelectedPermissionValue(e.row.data.valueType);
        }


    }

    return (
        <>
            <div className='card-header card-header-stretch overflow-auto'>
                <ul
                    className='nav nav-stretch nav-line-tabs fw-semibold border-transparent flex-nowrap'
                    role='tablist'
                >
                    <li className='nav-item'>
                        <a
                            className={clsx(`nav-link cursor-pointer`, { active: tab === 'Main' })}
                            onClick={() => setTab('Main')}
                            role='tab'
                        >
                            {t("definition")}
                        </a>
                    </li>
                    <li className='nav-item'>
                        <a
                            className={clsx(`nav-link cursor-pointer`, { active: tab === 'Permission' })}
                            onClick={() => selectedApp && setTab('Permission')}
                            role='tab'

                        >
                            {t("permissions")}
                        </a>
                    </li>
                    <li className='nav-item'>
                        <a
                            className={clsx(`nav-link cursor-pointer`, { active: tab === 'Menu' })}
                            onClick={() => selectedApp && setTab('Menu')}
                            role='tab'
                        >
                            {t("menus")}
                        </a>
                    </li>
                </ul>
            </div>

            <div className='card-body'>
                <div className='tab-content pt-3'>
                    <div className={clsx('tab-pane', { active: tab === 'Main' })}>
                        <FormWrapper
                            onSubmit={handleSubmit}
                            submitFormRef={submitFormRef}
                            formRef={formRef}
                            formData={role}
                            showValidationSummary={false}
                            simpleItemGroups={[
                                {
                                    useColCountByScreen: true,
                                    items: [
                                        {
                                            dataField: 'name',
                                            editorType: 'dxTextBox',
                                            editorOptions: nameEditorOptions,
                                            label: t('role_name'),
                                            isRequired: true,
                                            requiredMessage: 'Name is required',
                                            asyncValidation: {
                                                validationCallback: asyncRoleValidation,
                                                message: Required(t('name'))
                                            }
                                        },
                                        {
                                            dataField: 'description',
                                            editorType: 'dxTextBox',
                                            editorOptions: descriptionEditorOptions,
                                            label: t('role_description')
                                        },
                                        {
                                            dataField: 'applicationId',
                                            editorType: 'dxSelectBox',
                                            editorOptions: applicationEditorOptions,
                                            label: t('application'),
                                            isRequired: true,
                                            requiredMessage: Required(t('application'))
                                        },
                                        {
                                            dataField: 'accountId',
                                            editorType: 'dxSelectBox',
                                            editorOptions: accountEditorOptions,
                                            label: t('account'),
                                            isRequired: true,
                                            requiredMessage: Required(t('account'))
                                        }
                                    ]
                                }
                            ]}
                        >

                        </FormWrapper>

                    </div>
                    <div className={clsx('tab-pane', { active: tab === 'Permission' })}>
                        <div className='row mb-2 justify-content-end'>

                            <div className='col-auto'>
                                <DXButton
                                    text={t("add")}
                                    type='default'
                                    icon='add'
                                    onClick={showAddPermission}
                                />
                            </div>
                        </div>
                        <DataGrid
                            keyExpr="id"
                            height={500}
                            dataSource={rolePermissions}
                            showBorders={true}
                            remoteOperations={false}

                        >
                            <Paging enabled={false} />
                            <Editing
                                mode="cell"
                                allowUpdating={false}
                                allowDeleting={true}
                            />
                            <Grouping autoExpandAll={false} />


                            <Column
                                dataField={'subject'}
                                caption={''}
                                allowFiltering={false}
                                allowSorting={false}
                                groupCellTemplate={(element: any, data: any) => {
                                    element.innerHTML = data.displayValue;
                                }}
                                groupIndex={0}
                            />
                            <Column
                                dataField={'name'}
                                caption={t('permission')}
                                allowFiltering={false}
                                allowSorting={false}
                            />
                            <Column
                                dataField={'permissionValue'}
                                calculateCellValue={(rowData: any) => {
                                    return JSON.parse(rowData.permissionValue).value;
                                }}
                                allowEditing={true}
                                caption={t("permission_values")}
                                allowFiltering={false}
                                allowSorting={false}
                            />
                            <Column type="buttons"
                            >
                                <Button
                                    icon='edit'
                                    onClick={editRolePermission}

                                />
                                <Button
                                    name='delete'
                                    icon='minus'

                                />
                            </Column>


                        </DataGrid>
                        <Popup
                            visible={popupVisible}
                            onHiding={hideInfo}
                            showTitle={true}
                            width={1200}
                            height={630}
                            position={{
                                of: window,
                                at: 'center',
                                my: 'center'
                            }}
                            title={t("permissions")}
                        >

                            <Row>
                                <Col md={4}>
                                    <div className={'card'}>
                                        <TextBox
                                            value={searchTextBox}
                                            showClearButton={true}
                                            placeholder={t('search')}
                                            valueChangeEvent="keyup"
                                            onValueChanged={searchPermission}
                                        >

                                        </TextBox>
                                        <List
                                            dataSource={filteredPermissionSubjects}
                                            height={460}
                                            noDataText=''
                                            itemRender={ItemTemplate}
                                            selectionMode='single'
                                            onSelectedItemsChange={onSelectedItemsChange}
                                            collapsibleGroups={true}
                                            pageLoadMode={undefined}
                                            grouped={true}

                                            groupRender={GroupTemplate}
                                        ></List>
                                    </div>

                                </Col>
                                <Col md={8}>
                                    <div className={'card'} style={{ height: 495 }}>
                                        {selectedPermissionValue === "Actions" && (
                                            <DataGrid
                                                keyExpr="id"
                                                height={460}
                                                dataSource={permissions}
                                                showBorders={true}
                                                onEditorPreparing={onEditorPreparing}
                                                remoteOperations={false}
                                                onRowUpdated={onRowUpdating}
                                                columnAutoWidth={true}
                                            >
                                                <Paging enabled={false} />
                                                <Editing
                                                    mode="cell"
                                                    allowUpdating={true}
                                                />
                                                <Column
                                                    caption={selectedPermissionName}
                                                    alignment='center'
                                                >
                                                    <Column
                                                        dataField={'create'}
                                                        calculateCellValue={(rowData: any) => {
                                                            return rowData.create || false;
                                                        }}
                                                        dataType='boolean'
                                                        allowEditing={true}
                                                        caption={t("create")}
                                                        allowFiltering={false}
                                                        allowSorting={false}
                                                    />
                                                    <Column
                                                        dataField={'update'}
                                                        calculateCellValue={(rowData: any) => {
                                                            return rowData.update || false;
                                                        }}
                                                        dataType='boolean'
                                                        allowEditing={true}
                                                        caption={t("update")}
                                                        allowFiltering={false}
                                                        allowSorting={false}
                                                    />
                                                    <Column
                                                        dataField={'delete'}
                                                        calculateCellValue={(rowData: any) => {
                                                            return rowData.delete || false;
                                                        }}
                                                        dataType='boolean'
                                                        allowEditing={true}
                                                        caption={t("delete")}
                                                        allowFiltering={false}
                                                        allowSorting={false}
                                                    />
                                                    <Column
                                                        dataField={'read'}
                                                        calculateCellValue={(rowData: any) => {
                                                            return rowData.read || false;
                                                        }}
                                                        dataType='boolean'
                                                        allowEditing={true}
                                                        caption={t("read")}
                                                        allowFiltering={false}
                                                        allowSorting={false}
                                                    />
                                                    <Column
                                                        dataField={'list'}
                                                        calculateCellValue={(rowData: any) => {
                                                            return rowData.list || false;
                                                        }}
                                                        dataType='boolean'
                                                        allowEditing={true}
                                                        caption={t("list")}
                                                        allowFiltering={false}
                                                        allowSorting={false}
                                                    />
                                                </Column>

                                            </DataGrid>
                                        )
                                        }

                                        {selectedPermissionValue === "Number" && (
                                            <DataGrid
                                                keyExpr="id"
                                                height={600}
                                                dataSource={permissions}
                                                showBorders={true}
                                                onEditorPreparing={onEditorPreparing}
                                                remoteOperations={false}
                                                onRowUpdated={onRowUpdating}

                                            >
                                                <Paging enabled={false} />
                                                <Editing
                                                    mode="cell"
                                                    allowUpdating={true}
                                                />


                                                <Column
                                                    dataField={'name'}
                                                    caption={t('permission')}
                                                    allowEditing={false}
                                                    allowFiltering={false}
                                                    allowSorting={false}
                                                    fixed={true}
                                                />
                                                <Column
                                                    dataField={'value'}
                                                    dataType='number'
                                                    allowEditing={true}
                                                    caption={t('value')}
                                                    allowFiltering={false}
                                                    allowSorting={false}
                                                />

                                            </DataGrid>
                                        )
                                        }

                                        {selectedPermissionValue === "String" && (
                                            <DataGrid
                                                keyExpr="id"
                                                height={600}
                                                dataSource={permissions}
                                                showBorders={true}
                                                onEditorPreparing={onEditorPreparing}
                                                remoteOperations={false}
                                                onRowUpdated={onRowUpdating}

                                            >
                                                <Paging enabled={false} />
                                                <Editing
                                                    mode="cell"
                                                    allowUpdating={true}
                                                />


                                                <Column
                                                    dataField={'name'}
                                                    caption={t('permission')}
                                                    allowEditing={false}
                                                    allowFiltering={false}
                                                    allowSorting={false}
                                                    fixed={true}
                                                />
                                                <Column
                                                    dataField={'value'}
                                                    dataType='string'
                                                    allowEditing={true}
                                                    caption={t('value')}
                                                    allowFiltering={false}
                                                    allowSorting={false}
                                                />

                                            </DataGrid>
                                        )
                                        }
                                    </div>


                                </Col>
                            </Row>
                            <div className='row  pt-5 justify-content-end'>

                                <div className='col-auto'>
                                    <DXButton
                                        text={t("save")}
                                        type='default'
                                        onClick={addPermission}
                                        width={120}
                                        disabled={!saveButtonEnabled}
                                    />
                                </div>
                                <div className='col-auto'>
                                    <DXButton
                                        text={t("back")}
                                        type='normal'
                                        width={120}
                                        onClick={closeAddPermission}
                                    />
                                </div>
                            </div>
                        </Popup>

                    </div>

                    <div className={clsx('tab-pane', { active: tab === 'Menu' })}>
                        <Row>
                            <Col md={6}>
                                <TreeList
                                    id="menus"
                                    dataSource={menus}
                                    showRowLines={true}
                                    showBorders={true}
                                    columnAutoWidth={true}
                                    itemsExpr="items"
                                    dataStructure="tree"
                                >
                                    <TlEditing
                                        allowDeleting={false}
                                        confirmDelete={false}
                                        mode="row" />
                                    <Column
                                        dataField="name"
                                        caption={t("menus")} />
                                    <Column type="buttons">
                                        <Button
                                            icon='plus'
                                            onClick={selectMenu}
                                        />
                                    </Column>
                                </TreeList>
                            </Col>
                            <Col md={6}>
                                <TreeList
                                    id="selectedMenus"
                                    dataSource={selectedMenus}
                                    showRowLines={true}
                                    showBorders={true}
                                    columnAutoWidth={true}
                                    itemsExpr="items"
                                    dataStructure="tree"
                                >
                                    <TlEditing
                                        allowDeleting={false}
                                        confirmDelete={false}
                                        mode="row" />

                                    <Column type="buttons"
                                    >
                                        <Button
                                            icon='minus'
                                            onClick={deSelectMenu}

                                        />
                                    </Column>
                                    <Column
                                        dataField="name"
                                        caption={t('selected_menus')}
                                    />


                                </TreeList>
                            </Col>
                        </Row>
                    </div>
                </div>
            </div>

            <div className='card-footer py-6'>
                <div className='row'>
                    <div className='col-lg-3'></div>
                    <div className='col-lg-9 d-flex justify-content-end'>
                        <div className='col-auto'>
                            <DXButton
                                text={t("save")}
                                type='default'
                                width={120}
                                onClick={() => {
                                    if (formRef.current) {
                                        const validationResult = formRef.current.instance.validate();
                                        if (validationResult.isValid) {
                                            submitFormRef.current.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
                                        }
                                    }
                                }}
                            />
                        </div>
                    </div>
                </div>
            </div>

        </>

    );
}

export default RoleDefinition;
